import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, Input, SelectPicker } from 'rsuite';
import {
  ClubAlbumProviderList, ClubAlbumStatus, ClubAlbumStatusList, RunningClubAlbum,
  UpsertRunningClubExternalAlbum, isValidExternalAlbumUrl
} from '@/models/running-club-album.model';
import { upsertRunningClubExternalAlbum } from '@/services/running-club-album.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { getRunningClubEventsLookup } from '@/services/running-club-event.service';
import ImageUploader from '@/components/ImageUploader';
import { ObjectStorageFileCategory } from '@/models/storage.model';

const Textarea = React.forwardRef<HTMLTextAreaElement, any>((props, ref) => <Input {...props} as="textarea" ref={ref} />);

const modelSchema = Schema.Model<UpsertRunningClubExternalAlbum>({
  id: Schema.Types.NumberType(),
  runningClubId: Schema.Types.NumberType().isRequired('This field is required.'),
  name: Schema.Types.StringType().isRequired('This field is required.').maxLength(200),
  description: Schema.Types.StringType().maxLength(1000),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  externalProvider: Schema.Types.NumberType().isRequired('This field is required.'),
  externalUrl: Schema.Types.StringType()
    .isRequired('This field is required.')
    .maxLength(2000)
    .addRule(value => isValidExternalAlbumUrl(value), 'Must be a full http:// or https:// link.'),
});

interface RunningClubExternalAlbumDrawerProps extends DrawerProps {
  runningClubId?: number;
  /** Set to edit; must be an external album — the API rejects anything else. */
  album?: RunningClubAlbum;
  onSaved?: () => void;
}

/** Create/edit an external album. Internal albums are never editable from the panel. */
const RunningClubExternalAlbumDrawer: React.FC<RunningClubExternalAlbumDrawerProps> = ({ runningClubId, album, open, onClose, onSaved, ...rest }) => {
  const toModel = (): UpsertRunningClubExternalAlbum => ({
    id: album?.id,
    runningClubId: album?.runningClub?.id ?? runningClubId,
    runningClubEventId: album?.runningClubEvent?.id,
    name: album?.name,
    description: album?.description,
    coverImage: album?.coverImage,
    status: album?.status ?? ClubAlbumStatus.Active,
    externalUrl: album?.externalUrl,
    externalProvider: album?.externalProvider,
  });

  const [formValue, setFormValue] = useState<UpsertRunningClubExternalAlbum>(toModel());
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue(toModel());
    setErrorModel(undefined);
  }, [runningClubId, album, open]);

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setErrorModel(undefined);
    setLoading(false);
    if (onClose) {
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunningClubAlbum> = await upsertRunningClubExternalAlbum({
        ...formValue,
        externalUrl: formValue.externalUrl?.trim(),
      });
      if (result.success) {
        onSaved?.();
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right"
      open={open}
      onClose={handleDrawerClose}
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{album ? 'Edit external album' : 'Add an external album'}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-external-album' fluid ref={formRef}
          onChange={value => setFormValue(value as UpsertRunningClubExternalAlbum)}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Running Club</Form.Label>
            <Form.Control name="runningClubId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubsLookup(text, page)}
              disabled={!!runningClubId || !!album}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Event (optional)</Form.Label>
            <Form.Control name="runningClubEventId" block
              accepter={FetchingSelect}
              fetchData={(text, page) => getRunningClubEventsLookup(text, formValue.runningClubId, page)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control name="name" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control name="description" rows={3} accepter={Textarea} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Hosted on</Form.Label>
            <Form.Control name="externalProvider" block searchable={false}
              accepter={SelectPicker}
              data={ClubAlbumProviderList}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Album link</Form.Label>
            <Form.Control name="externalUrl" placeholder="https://photos.app.goo.gl/..." />
            <Form.HelpText>Must be shared so club members can open it.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Control name="status" block searchable={false} cleanable={false}
              accepter={SelectPicker}
              data={ClubAlbumStatusList}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Cover (optional)</Form.Label>
            <Form.Control
              accepter={ImageUploader}
              name="coverImage"
              height="200px"
              url={formValue.coverImage}
              accept=".jpg,.jpeg,.png"
              objectStorageCategory={ObjectStorageFileCategory.RunningClub}
              onChange={url => setFormValue(pre => ({ ...pre, coverImage: url }))}
            />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default RunningClubExternalAlbumDrawer;
